import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table2, Lock, ChevronRight, Upload, Bookmark, Trash2, Pencil, Check, X } from "lucide-react";
import { fetchTables } from "../../services/sadtService";
import { useSavedTablesStore } from "../../stores/savedTablesStore";
import { toast } from "../../stores/toastStore";
import { confirm } from "../../components/ConfirmDialog";

function TableRow({ name, readOnly, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-[var(--color-paper-2)] transition-colors border-b border-[var(--color-rule)] last:border-0"
    >
      {readOnly
        ? <Lock size={14} className="text-[var(--color-ink-3)] shrink-0" />
        : <Table2 size={14} className="text-[var(--color-ink-2)] shrink-0" />
      }
      <span className="mono flex-1" style={{ fontSize: "var(--text-sm)" }}>{name}</span>
      {badge && (
        <span className="text-[9px] font-medium tracking-widest uppercase text-[var(--color-ink-3)] border border-[var(--color-rule-strong)] px-2 py-0.5">
          {badge}
        </span>
      )}
      <ChevronRight
        size={14}
        className="text-[var(--color-rule-strong)] group-hover:text-[var(--color-ink-3)] transition-colors"
      />
    </button>
  );
}

function SavedTableRow({ table, onNavigate, onDelete, onRename }) {
  const [editing,   setEditing]   = useState(false);
  const [nameValue, setNameValue] = useState(table.name);

  function commitRename() {
    const name = nameValue.trim();
    if (name && name !== table.name) onRename(table.id, name);
    setEditing(false);
  }

  function handleKey(e) {
    if (e.key === "Enter")  { e.preventDefault(); commitRename(); }
    if (e.key === "Escape") { setEditing(false); setNameValue(table.name); }
  }

  return (
    <div className="group flex items-center gap-2 px-5 py-3 border-b border-[var(--color-rule)] last:border-0 hover:bg-[var(--color-paper-2)] transition-colors">
      <Bookmark size={13} className="text-[var(--color-ink-3)] shrink-0" />

      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onNavigate(table.id)}>
        {editing ? (
          <input
            autoFocus
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onKeyDown={handleKey}
            onBlur={commitRename}
            className="w-full bg-transparent border-b border-[var(--color-accent)] outline-none"
            style={{ fontFamily: "var(--font-sans)", fontSize: "var(--text-sm)" }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="block truncate"
            style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}
          >
            {table.name}
          </span>
        )}
        {table.sourceQuery && (
          <span
            className="mono block truncate"
            style={{ fontSize: "9px", color: "var(--color-ink-3)", marginTop: "1px" }}
            title={table.sourceQuery}
          >
            {table.sourceQuery}
          </span>
        )}
      </div>

      <span className="mono shrink-0" style={{ fontSize: "9px", color: "var(--color-ink-3)" }}>
        {formatDate(table.savedAt)}
      </span>

      {/* Action buttons — visible on hover */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        {editing ? (
          <>
            <button
              onClick={commitRename}
              className="p-1.5 rounded-sm hover:bg-[var(--color-rule)] transition-colors text-[var(--color-status-even)]"
              title="Confirm rename"
            >
              <Check size={12} />
            </button>
            <button
              onClick={() => { setEditing(false); setNameValue(table.name); }}
              className="p-1.5 rounded-sm hover:bg-[var(--color-rule)] transition-colors text-[var(--color-ink-3)]"
              title="Cancel"
            >
              <X size={12} />
            </button>
          </>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className="p-1.5 rounded-sm hover:bg-[var(--color-rule)] transition-colors text-[var(--color-ink-3)]"
            title="Rename"
          >
            <Pencil size={12} />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(table.id); }}
          className="p-1.5 rounded-sm hover:bg-[var(--color-rule)] transition-colors text-[var(--color-ink-3)] hover:text-[var(--color-accent)]"
          title="Delete saved table"
        >
          <Trash2 size={12} />
        </button>
        <ChevronRight
          size={13}
          className="text-[var(--color-rule-strong)] group-hover:text-[var(--color-ink-3)] transition-colors cursor-pointer"
          onClick={() => onNavigate(table.id)}
        />
      </div>
    </div>
  );
}

export default function DataIndexPage() {
  const navigate = useNavigate();
  const [tables,           setTables]           = useState([]);
  const [calculatedTables, setCalculatedTables] = useState([]);
  const [loading,          setLoading]          = useState(true);

  const savedTables  = useSavedTablesStore((s) => s.tables);
  const deleteTable  = useSavedTablesStore((s) => s.deleteTable);
  const renameTable  = useSavedTablesStore((s) => s.renameTable);

  useEffect(() => {
    fetchTables()
      .then((d) => {
        setTables(d.tables || []);
        setCalculatedTables(d.calculated_tables || []);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  const sourceTables = tables.filter((t) => !calculatedTables.includes(t));
  const calcTables   = tables.filter((t) =>  calculatedTables.includes(t));

  async function handleDeleteSaved(id) {
    const ok = await confirm({
      title: "Delete this saved table?",
      description: "It will be removed from My Tables permanently.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    deleteTable(id);
    toast.success("Saved table deleted");
  }

  function handleRenameSaved(id, newName) {
    renameTable(id, newName);
    toast.success("Table renamed");
  }

  if (loading) {
    return (
      <div className="px-8 lg:px-12 pt-12 text-[var(--color-ink-3)]" style={{ fontSize: "var(--text-sm)" }}>
        Loading tables…
      </div>
    );
  }

  return (
    <div className="px-8 lg:px-12 pt-10 pb-16 max-w-4xl">
      <div className="reveal">
        <div className="eyebrow">DATA · TABLES</div>
      </div>
      <h1
        className="reveal display mt-4"
        style={{ fontSize: "var(--text-3xl)", animationDelay: "120ms" }}
      >
        Data
      </h1>
      <p
        className="reveal mt-2 mb-10"
        style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)", animationDelay: "180ms" }}
      >
        {tables.length} tables · {sourceTables.length} editable · {calcTables.length} calculated
        {savedTables.length > 0 && ` · ${savedTables.length} saved from chat`}
      </p>

      {/* ── My Tables · Saved from Chat ────────────────────────────────── */}
      {savedTables.length > 0 && (
        <div className="reveal mb-8" style={{ animationDelay: "220ms" }}>
          <div className="eyebrow mb-3" style={{ fontSize: "9px" }}>MY TABLES · SAVED FROM CHAT</div>
          <div className="border border-[var(--color-rule)] overflow-hidden">
            {savedTables.map((t) => (
              <SavedTableRow
                key={t.id}
                table={t}
                onNavigate={(id) => navigate(`/app/data/saved/${id}`)}
                onDelete={handleDeleteSaved}
                onRename={handleRenameSaved}
              />
            ))}
          </div>
        </div>
      )}

      {tables.length === 0 && savedTables.length === 0 && (
        <div
          className="reveal border border-[var(--color-rule)] px-6 py-10 text-center"
          style={{ animationDelay: "240ms" }}
        >
          <Upload size={24} className="mx-auto mb-3 text-[var(--color-ink-3)]" />
          <p style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-3)" }}>
            No tables yet — import a workbook to get started.
          </p>
        </div>
      )}

      {savedTables.length === 0 && tables.length === 0 ? null : savedTables.length === 0 && (
        <div
          className="reveal mb-8 border border-dashed border-[var(--color-rule)] px-5 py-4"
          style={{ animationDelay: "220ms" }}
        >
          <div className="eyebrow mb-1.5" style={{ fontSize: "9px" }}>MY TABLES · SAVED FROM CHAT</div>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)" }}>
            No saved tables yet. Expand a table in the chat and click "Save table" to add it here.
          </p>
        </div>
      )}

      {/* ── Source Data ────────────────────────────────────────────────── */}
      {sourceTables.length > 0 && (
        <div className="reveal mb-8" style={{ animationDelay: "240ms" }}>
          <div className="eyebrow mb-3" style={{ fontSize: "9px" }}>SOURCE DATA</div>
          <div className="border border-[var(--color-rule)] overflow-hidden">
            {sourceTables.map((t) => (
              <TableRow
                key={t}
                name={t}
                readOnly={false}
                onClick={() => navigate(`/app/data/${encodeURIComponent(t)}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Calculated Tables ──────────────────────────────────────────── */}
      {calcTables.length > 0 && (
        <div className="reveal" style={{ animationDelay: "300ms" }}>
          <div className="eyebrow mb-3" style={{ fontSize: "9px" }}>CALCULATED · READ-ONLY</div>
          <div className="border border-[var(--color-rule)] overflow-hidden">
            {calcTables.map((t) => (
              <TableRow
                key={t}
                name={t}
                readOnly={true}
                badge="CALC"
                onClick={() => navigate(`/app/data/${encodeURIComponent(t)}`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
